import { Component } from '@opten/gin-tonic/helpers/compiler';

const ENDPOINT = '/umbraco/surface/blog/getpaged';

@Component({ className: 'js-blog-overview' })
export class BlogOverviewComponent {
	private id: string;
	private culture: string;

	private filterEl: HTMLElement | null;
	private toggleEl: HTMLButtonElement | null = null;
	private dropdownEl: HTMLElement | null = null;
	private checkboxes: HTMLInputElement[] = [];
	private resetEl: HTMLButtonElement | null = null;
	private applyEl: HTMLButtonElement | null = null;
	private resetAllEl: HTMLButtonElement | null = null;
	private pillsEl: HTMLElement | null;
	private searchEl: HTMLInputElement | null;
	private listEl: HTMLElement | null;
	private loadMoreEl: HTMLButtonElement | null;

	private selectedCategoryIds = new Set<number>();
	private searchTerm = '';
	private page = 1;
	private hasNext = false;
	private searchDebounce: number | undefined;

	constructor(private element: HTMLElement) {
		this.id = element.getAttribute('data-blog-id') || '';
		this.culture = element.getAttribute('data-blog-culture') || '';

		this.filterEl = element.querySelector('[data-blog-filter]');
		this.toggleEl = element.querySelector('[data-blog-filter-toggle]');
		this.dropdownEl = element.querySelector('[data-blog-filter-dropdown]');
		this.checkboxes = Array.from(
			element.querySelectorAll('[data-blog-filter-checkbox]')
		) as HTMLInputElement[];
		this.resetEl = element.querySelector('[data-blog-filter-reset]');
		this.applyEl = element.querySelector('[data-blog-filter-apply]');
		this.resetAllEl = element.querySelector('[data-blog-reset-all]');
		this.pillsEl = element.querySelector('[data-blog-filter-pills]');
		this.searchEl = element.querySelector('[data-blog-search-input]');
		this.listEl = element.querySelector('[data-blog-overview-list]');
		this.loadMoreEl = element.querySelector('[data-blog-load-more]');

		this.selectedCategoryIds = new Set(
			this.checkboxes.filter((c) => c.checked).map((c) => parseInt(c.value, 10))
		);
		this.searchTerm = this.searchEl?.value || '';
		this.hasNext = this.loadMoreEl ? !this.loadMoreEl.hidden : false;

		this.bind();
	}

	private bind() {
		this.toggleEl?.addEventListener('click', () => this.toggleDropdown());
		document.addEventListener('click', (e) => {
			if (
				this.filterEl &&
				!this.filterEl.contains(e.target as Node) &&
				this.dropdownEl &&
				!this.dropdownEl.hidden
			) {
				this.closeDropdown();
			}
		});

		this.applyEl?.addEventListener('click', () => {
			this.selectedCategoryIds = new Set(
				this.checkboxes
					.filter((c) => c.checked)
					.map((c) => parseInt(c.value, 10))
			);
			this.closeDropdown();
			this.renderPills();
			this.refresh();
		});

		this.resetEl?.addEventListener('click', () => {
			this.checkboxes.forEach((c) => (c.checked = false));
			this.selectedCategoryIds.clear();
			this.closeDropdown();
			this.renderPills();
			this.refresh();
		});

		this.pillsEl?.addEventListener('click', (e) => {
			const pill = (e.target as HTMLElement).closest(
				'[data-blog-filter-pill]'
			) as HTMLElement | null;
			if (!pill) return;
			const value = parseInt(pill.getAttribute('data-value') || '', 10);
			this.selectedCategoryIds.delete(value);
			const checkbox = this.checkboxes.find(
				(c) => parseInt(c.value, 10) === value
			);
			if (checkbox) checkbox.checked = false;
			this.renderPills();
			this.refresh();
		});

		this.searchEl?.addEventListener('input', () => {
			window.clearTimeout(this.searchDebounce);
			this.searchDebounce = window.setTimeout(() => {
				this.searchTerm = this.searchEl?.value || '';
				this.refresh();
			}, 350);
		});

		this.resetAllEl?.addEventListener('click', () => {
			this.checkboxes.forEach((c) => (c.checked = false));
			this.selectedCategoryIds.clear();
			this.searchTerm = '';
			if (this.searchEl) this.searchEl.value = '';
			this.closeDropdown();
			this.renderPills();
			this.refresh();
		});

		this.loadMoreEl?.addEventListener('click', () => this.loadMore());
	}

	private toggleDropdown() {
		if (!this.dropdownEl || !this.toggleEl) return;
		if (this.dropdownEl.hidden) {
			this.openDropdown();
		} else {
			this.closeDropdown();
		}
	}

	private openDropdown() {
		if (!this.dropdownEl || !this.toggleEl) return;
		this.dropdownEl.hidden = false;
		this.toggleEl.setAttribute('aria-expanded', 'true');
	}

	private closeDropdown() {
		if (!this.dropdownEl || !this.toggleEl) return;
		this.dropdownEl.hidden = true;
		this.toggleEl.setAttribute('aria-expanded', 'false');
	}

	private renderPills() {
		if (!this.pillsEl) return;
		this.pillsEl.innerHTML = '';
		this.selectedCategoryIds.forEach((id) => {
			const checkbox = this.checkboxes.find((c) => parseInt(c.value, 10) === id);
			const name = checkbox?.getAttribute('data-name') || '';

			const pill = document.createElement('button');
			pill.type = 'button';
			pill.className = 'c-blog-pill';
			pill.setAttribute('data-blog-filter-pill', '');
			pill.setAttribute('data-value', String(id));

			const label = document.createElement('span');
			label.textContent = name;
			pill.appendChild(label);

			const close = document.createElement('span');
			close.className = 'c-blog-pill__close';
			close.setAttribute('aria-hidden', 'true');
			close.textContent = '×';
			pill.appendChild(close);

			this.pillsEl!.appendChild(pill);
		});
	}

	private buildQuery(page: number): string {
		const params = new URLSearchParams();
		params.set('id', this.id);
		if (this.culture) params.set('culture', this.culture);
		if (this.searchTerm) params.set('q', this.searchTerm);
		this.selectedCategoryIds.forEach((id) => params.append('filter', String(id)));
		params.set('page', String(page));
		return params.toString();
	}

	private syncUrl() {
		const url = new URL(window.location.href);
		if (this.selectedCategoryIds.size) {
			const names = Array.from(this.selectedCategoryIds)
				.map((id) => {
					const checkbox = this.checkboxes.find(
						(c) => parseInt(c.value, 10) === id
					);
					const name = checkbox?.getAttribute('data-name') || '';
					return name.replace(/\s+/g, '').toLowerCase();
				})
				.filter((name) => name);
			url.searchParams.set('filter', names.join(','));
		} else {
			url.searchParams.delete('filter');
		}
		if (this.searchTerm) {
			url.searchParams.set('q', this.searchTerm);
		} else {
			url.searchParams.delete('q');
		}
		const search = url.searchParams.toString().replace(/%2C/g, ',');
		history.replaceState(
			null,
			'',
			`${url.pathname}${search ? `?${search}` : ''}${url.hash}`
		);
	}

	private setLoadMoreVisibility() {
		if (!this.loadMoreEl) return;
		this.loadMoreEl.hidden = !this.hasNext;
	}

	private async refresh() {
		this.page = 1;
		this.syncUrl();
		const response = await fetch(`${ENDPOINT}?${this.buildQuery(this.page)}`);
		const html = await response.text();
		this.hasNext = response.headers.get('X-Has-Next') === 'True';
		if (this.listEl) this.listEl.innerHTML = html;
		this.setLoadMoreVisibility();
	}

	private async loadMore() {
		const nextPage = this.page + 1;
		const response = await fetch(`${ENDPOINT}?${this.buildQuery(nextPage)}`);
		const html = await response.text();
		this.page = nextPage;
		this.hasNext = response.headers.get('X-Has-Next') === 'True';
		if (this.listEl) this.listEl.insertAdjacentHTML('beforeend', html);
		this.setLoadMoreVisibility();
	}
}
